import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { LucideIcon } from 'lucide-react';
import type {
  Transaction,
  Bill,
  SavingsGoal,
  SavingsContribution,
  Category,
  Client,
  Product,
  Quote,
  Loan,
  BusinessBudget,
  BusinessRevenue,
  BusinessExpense,
  SalesReceipt,
  DeliveryNote,
  SavingsGroup,
  GroupMember,
  GroupInvitation,
  GroupContribution,
  GroupActivity,
  Project,
  Invoice,
  ClientPayment,
  ClientExpense,
  CommunicationLog,
  TaskNote,
} from './types';
import { dbSync } from './supabase-sync';

const generateId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `id_${Date.now()}_${Math.random().toString(16).slice(2)}`;
};

// ============================================
// Initial Data
// ============================================

export const initialCategories: Array<Omit<Category, 'id' | 'icon'>> = [
  // Personal Expense Categories
  { name: 'Housing', color: '#2563eb', type: 'expense', workspace: 'personal', budget: 1200, budgetFrequency: 'monthly' },
  { name: 'Transportation', color: '#ca8a04', type: 'expense', workspace: 'personal', budget: 300, budgetFrequency: 'monthly' },
  { name: 'Food & Groceries', color: '#16a34a', type: 'expense', workspace: 'personal', budget: 400, budgetFrequency: 'monthly' },
  { name: 'Utilities', color: '#9333ea', type: 'expense', workspace: 'personal', budget: 150, budgetFrequency: 'monthly' },
  { name: 'Healthcare', color: '#dc2626', type: 'expense', workspace: 'personal', budget: 200, budgetFrequency: 'monthly' },
  { name: 'Entertainment', color: '#db2777', type: 'expense', workspace: 'personal', budget: 100, budgetFrequency: 'monthly' },
  { name: 'Personal Care', color: '#f97316', type: 'expense', workspace: 'personal', budget: 75, budgetFrequency: 'monthly' },
  
  // Personal Income Categories
  { name: 'Salary', color: '#15803d', type: 'income', workspace: 'personal' },
  { name: 'Freelance', color: '#0d9488', type: 'income', workspace: 'personal' },
  { name: 'Investment', color: '#1d4ed8', type: 'income', workspace: 'personal' },

  // Business Expense Categories
  { name: 'Office Supplies', color: '#4f46e5', type: 'expense', workspace: 'business', budget: 200, budgetFrequency: 'monthly' },
  { name: 'Software & Tools', color: '#0891b2', type: 'expense', workspace: 'business', budget: 150, budgetFrequency: 'monthly' },
  { name: 'Marketing', color: '#be185d', type: 'expense', workspace: 'business', budget: 500, budgetFrequency: 'monthly' },
  { name: 'Travel', color: '#c2410c', type: 'expense', workspace: 'business', budget: 300, budgetFrequency: 'monthly' },
  { name: 'Employee Salaries', color: '#b91c1c', type: 'expense', workspace: 'business', budget: 5000, budgetFrequency: 'monthly' },

  // Business Income Categories
  { name: 'Product Sales', color: '#047857', type: 'income', workspace: 'business' },
  { name: 'Service Fees', color: '#0e7490', type: 'income', workspace: 'business' },
  { name: 'Consulting', color: '#312e81', type: 'income', workspace: 'business' },
];


// ============================================
// Store Interface
// ============================================

interface AppState {
  transactions: Transaction[];
  bills: Bill[];
  savingsGoals: SavingsGoal[];
  clients: Client[];
  products: Product[];
  quotes: Quote[];
  loans: Loan[];
  categories: Category[];
  salesReceipts: SalesReceipt[];
  deliveryNotes: DeliveryNote[];
  businessBudgets: BusinessBudget[];
  businessRevenues: BusinessRevenue[];
  businessExpenses: BusinessExpense[];
  // Group Savings
  savingsGroups: SavingsGroup[];
  groupMembers: GroupMember[];
  groupInvitations: GroupInvitation[];
  groupContributions: GroupContribution[];
  groupActivities: GroupActivity[];
  // CRM Properties
  projects: Project[];
  invoices: Invoice[];
  payments: ClientPayment[];
  expenses: ClientExpense[];
  communications: CommunicationLog[];
  tasks: TaskNote[];
  isLoading: boolean;
  
  // Methods
  setSyncData: (key: string, data: any[]) => void;

  // Transaction methods
  addTransaction: (transaction: Omit<Transaction, 'id' | 'category'> & { category_id: string }) => Promise<void>;
  updateTransaction: (transaction: Omit<Transaction, 'category'> & { category_id: string }) => Promise<void>;
  deleteTransaction: (id: string) => Promise<void>;

  // Bill methods
  addBill: (bill: Omit<Bill, 'id'>) => Promise<void>;
  updateBill: (bill: Bill) => Promise<void>;
  deleteBill: (id: string) => Promise<void>;

  // Savings Goal methods
  addSavingsGoal: (goal: Omit<SavingsGoal, 'id'>) => Promise<void>;
  updateSavingsGoal: (goal: SavingsGoal) => Promise<void>;
  updateSavingsGoalComplete: (goal: SavingsGoal) => Promise<void>;
  deleteSavingsGoal: (id: string) => Promise<void>;
  savingsContributions: SavingsContribution[];
  addSavingsContribution: (contribution: Omit<SavingsContribution, 'id'>) => Promise<void>;
  deleteSavingsContribution: (id: string, goalId: string, amount: number) => Promise<void>;

  // Category methods
  addCategory: (category: Omit<Category, 'id' | 'icon'>) => Promise<void>;
  updateCategory: (category: Category) => Promise<void>;
  deleteCategory: (id: string) => Promise<void>;

  // Client methods
  addClient: (client: Omit<Client, 'id'>) => Promise<void>;
  updateClient: (client: Client) => Promise<void>;
  deleteClient: (id: string) => Promise<void>;

  // Product methods
  addProduct: (product: Omit<Product, 'id'>) => Promise<void>;
  updateProduct: (product: Product) => Promise<void>;
  deleteProduct: (id: string) => Promise<void>;

  // Quote methods
  addQuote: (quote: Omit<Quote, 'id'>) => Promise<void>;
  updateQuote: (quote: Quote) => Promise<void>;
  deleteQuote: (id: string) => Promise<void>;

  // Loan methods
  addLoan: (loan: Omit<Loan, 'id'>) => Promise<void>;
  updateLoan: (loan: Loan) => Promise<void>;
  deleteLoan: (id: string) => Promise<void>;

  // Business Budget methods
  addBusinessBudget: (budget: Omit<BusinessBudget, 'id'>) => Promise<void>;
  updateBusinessBudget: (budget: BusinessBudget) => Promise<void>;
  deleteBusinessBudget: (id: string) => Promise<void>;

  // Business Revenue methods
  addBusinessRevenue: (revenue: Omit<BusinessRevenue, 'id'>) => Promise<void>;
  updateBusinessRevenue: (revenue: BusinessRevenue) => Promise<void>;
  deleteBusinessRevenue: (id: string) => Promise<void>;

  // Business Expense methods
  addBusinessExpense: (expense: Omit<BusinessExpense, 'id'>) => Promise<void>;
  updateBusinessExpense: (expense: BusinessExpense) => Promise<void>;
  deleteBusinessExpense: (id: string) => Promise<void>;

  // Group Savings methods
  addSavingsGroup: (group: Omit<SavingsGroup, 'id'>) => Promise<void>;
  updateSavingsGroup: (group: SavingsGroup) => Promise<void>;
  deleteSavingsGroup: (id: string) => Promise<void>;

  addGroupMember: (member: Omit<GroupMember, 'id'>) => Promise<void>;
  updateGroupMember: (member: GroupMember) => Promise<void>;
  deleteGroupMember: (id: string) => Promise<void>;

  addGroupInvitation: (invitation: Omit<GroupInvitation, 'id'>) => Promise<void>;
  updateGroupInvitation: (invitation: GroupInvitation) => Promise<void>;
  deleteGroupInvitation: (id: string) => Promise<void>;

  addGroupContribution: (contribution: Omit<GroupContribution, 'id'>) => Promise<void>;
  updateGroupContribution: (contribution: GroupContribution) => Promise<void>;
  deleteGroupContribution: (id: string) => Promise<void>;

  addGroupActivity: (activity: Omit<GroupActivity, 'id'>) => Promise<void>;
  updateGroupActivity: (activity: GroupActivity) => Promise<void>;
  deleteGroupActivity: (id: string) => Promise<void>;

  // Project methods
  addProject: (project: Omit<Project, 'id'>) => Promise<void>;
  updateProject: (project: Project) => Promise<void>;
  deleteProject: (id: string) => Promise<void>;

  // Invoice methods
  addInvoice: (invoice: Omit<Invoice, 'id'>) => Promise<void>;
  updateInvoice: (invoice: Invoice) => Promise<void>;
  deleteInvoice: (id: string) => Promise<void>;

  // Client Payment methods
  addClientPayment: (payment: Omit<ClientPayment, 'id'>) => Promise<void>;
  updateClientPayment: (payment: ClientPayment) => Promise<void>;
  deleteClientPayment: (id: string) => Promise<void>;

  // Client Expense methods
  addClientExpense: (expense: Omit<ClientExpense, 'id'>) => Promise<void>;
  updateClientExpense: (expense: ClientExpense) => Promise<void>;
  deleteClientExpense: (id: string) => Promise<void>;

  // Communication Log methods
  addCommunicationLog: (log: Omit<CommunicationLog, 'id'>) => Promise<void>;
  updateCommunicationLog: (log: CommunicationLog) => Promise<void>;
  deleteCommunicationLog: (id: string) => Promise<void>;

  // Task Note methods
  addTaskNote: (task: Omit<TaskNote, 'id'>) => Promise<void>;
  updateTaskNote: (task: TaskNote) => Promise<void>;
  deleteTaskNote: (id: string) => Promise<void>;
}

// ============================================
// Store Implementation - Supabase-First Architecture
// ============================================

export const useAppStore = create<AppState>()(
  persist(
    (set, get) => ({
      // ============================================
      // Initial State
      // ============================================
      transactions: [],
      bills: [],
      savingsGoals: [],
      savingsContributions: [],
      clients: [],
      products: [],
      quotes: [],
      loans: [],
      categories: [],
      salesReceipts: [],
      deliveryNotes: [],
      businessBudgets: [],
      businessRevenues: [],
      businessExpenses: [],
      savingsGroups: [],
      groupMembers: [],
      groupInvitations: [],
      groupContributions: [],
      groupActivities: [],
      projects: [],
      invoices: [],
      payments: [],
      expenses: [],
      communications: [],
      tasks: [],
      isLoading: true,

      // ============================================
      // Methods
      // ============================================

      setSyncData: (key: string, data: any[]) => {
        if (!data || data.length === 0) {
          // Don't wipe existing data with an empty fetch result
          // Only update isLoading
          set({ isLoading: false } as Partial<AppState>);
          return;
        }
        
        const currentState = get();
        const currentData = (currentState as any)[key] as any[] | undefined;
        
        if (!currentData || currentData.length === 0) {
          // Nothing local, just set directly
          set({ [key]: data, isLoading: false } as Partial<AppState>);
          return;
        }
        
        // Merge: server wins on conflict (by id), but keep local records
        // that server doesn't have yet (optimistic creates not yet confirmed)
        const serverIds = new Set(data.map((d: any) => d.id));
        const localOnlyRecords = currentData.filter((local: any) => !serverIds.has(local.id));
        
        set({
          [key]: [...data, ...localOnlyRecords],
          isLoading: false,
        } as Partial<AppState>);
      },

      // --------------------------------------------
      // Transactions - Supabase First
      // --------------------------------------------
      addTransaction: async (newTransaction) => {
        const category = get().categories.find(c => c.id === newTransaction.category_id);
        if (!category) throw new Error('Category not found');
        
        const transaction: Transaction = {
          id: generateId(),
          ...newTransaction,
          category: category.name,
        };
        
        // Update local state FIRST for immediate UI feedback
        set(state => ({ transactions: [transaction, ...state.transactions] }));
        
        // Then sync to Supabase in background
        try {
          await dbSync.syncTransaction(transaction, 'create');
        } catch (error) {
          console.error('Failed to sync transaction to Supabase:', error);
          // Revert local state if sync fails
          set(state => ({ transactions: state.transactions.filter(t => t.id !== transaction.id) }));
          throw error;
        }
      },

      updateTransaction: async (updatedTransaction) => {
        const category = get().categories.find(c => c.id === updatedTransaction.category_id);
        if (!category) throw new Error('Category not found');

        const transaction: Transaction = {
          ...updatedTransaction,
          category: category.name,
        };

        // Update local state FIRST
        set(state => ({
          transactions: state.transactions.map(t => t.id === transaction.id ? transaction : t),
        }));

        // Then sync to Supabase
        try {
          await dbSync.syncTransaction(transaction, 'update');
        } catch (error) {
          console.error('Failed to update transaction in Supabase:', error);
          throw error;
        }
      },

      deleteTransaction: async (id: string) => {
        const transactionToDelete = get().transactions.find(t => t.id === id);
        if (!transactionToDelete) return;
        
        // Update local state FIRST
        set(state => ({ transactions: state.transactions.filter(t => t.id !== id) }));
        
        // Then sync to Supabase
        try {
          await dbSync.syncTransaction(transactionToDelete, 'delete');
        } catch (error) {
          console.error('Failed to delete transaction from Supabase:', error);
          // Re-add the transaction if delete fails
          set(state => ({ transactions: [transactionToDelete, ...state.transactions] }));
          throw error;
        }
      },

      // --------------------------------------------
      // Bills - Supabase First
      // --------------------------------------------
      addBill: async (newBill) => {
        const bill: Bill = { id: generateId(), ...newBill };
        set(state => ({ bills: [bill, ...state.bills] }));
        try {
          await dbSync.syncBill(bill, 'create');
        } catch (error) {
          console.error('Failed to sync bill to Supabase:', error);
          set(state => ({ bills: state.bills.filter(b => b.id !== bill.id) }));
          throw error;
        }
      },

      updateBill: async (updatedBill) => {
        set(state => ({ 
          bills: state.bills.map(b => b.id === updatedBill.id ? updatedBill : b)
        }));
        try {
          await dbSync.syncBill(updatedBill, 'update');
        } catch (error) {
          console.error('Failed to update bill in Supabase:', error);
          throw error;
        }
      },

      deleteBill: async (id: string) => {
        const billToDelete = get().bills.find(b => b.id === id);
        if (!billToDelete) return;
        set(state => ({ bills: state.bills.filter(b => b.id !== id) }));
        try {
          await dbSync.syncBill(billToDelete, 'delete');
        } catch (error) {
          console.error('Failed to delete bill from Supabase:', error);
          set(state => ({ bills: [billToDelete, ...state.bills] }));
          throw error;
        }
      },

      // --------------------------------------------
      // Savings Goals - Supabase First
      // --------------------------------------------
      addSavingsGoal: async (newGoal) => {
        const goal: SavingsGoal = { id: generateId(), ...newGoal };
        set(state => ({ savingsGoals: [goal, ...state.savingsGoals] }));
        try {
          await dbSync.syncSavingsGoal(goal, 'create');
        } catch (error) {
          console.error('Failed to sync savings goal to Supabase:', error);
          set(state => ({ savingsGoals: state.savingsGoals.filter(g => g.id !== goal.id) }));
          throw error;
        }
      },

      updateSavingsGoal: async (updatedGoal) => {
        set(state => ({ 
          savingsGoals: state.savingsGoals.map(g => g.id === updatedGoal.id ? updatedGoal : g)
        }));
        try {
          await dbSync.syncSavingsGoal(updatedGoal, 'update');
        } catch (error) {
          console.error('Failed to update savings goal in Supabase:', error);
          throw error;
        }
      },

      updateSavingsGoalComplete: async (updatedGoal) => {
        set(state => ({ 
          savingsGoals: state.savingsGoals.map(g => g.id === updatedGoal.id ? updatedGoal : g)
        }));
        try {
          await dbSync.syncSavingsGoal(updatedGoal, 'update');
        } catch (error) {
          console.error('Failed to update savings goal in Supabase:', error);
          throw error;
        }
      },

      deleteSavingsGoal: async (id: string) => {
        const goalToDelete = get().savingsGoals.find(g => g.id === id);
        if (!goalToDelete) return;
        set(state => ({ savingsGoals: state.savingsGoals.filter(g => g.id !== id) }));
        try {
          await dbSync.syncSavingsGoal(goalToDelete, 'delete');
        } catch (error) {
          console.error('Failed to delete savings goal from Supabase:', error);
          set(state => ({ savingsGoals: [goalToDelete, ...state.savingsGoals] }));
          throw error;
        }
      },

      addSavingsContribution: async (newContribution) => {
        const contribution: SavingsContribution = { id: generateId(), ...newContribution };
        // Update goal currentAmount
        const goal = get().savingsGoals.find(g => g.id === newContribution.goalId);
        if (goal) {
          const updatedGoal = { ...goal, currentAmount: goal.currentAmount + newContribution.amount };
          set(state => ({
            savingsContributions: [contribution, ...state.savingsContributions],
            savingsGoals: state.savingsGoals.map(g => g.id === goal.id ? updatedGoal : g),
          }));
          try { await dbSync.syncSavingsGoal(updatedGoal, 'update'); } catch (e) { console.error(e); }
        } else {
          set(state => ({ savingsContributions: [contribution, ...state.savingsContributions] }));
        }
      },

      deleteSavingsContribution: async (id, goalId, amount) => {
        const goal = get().savingsGoals.find(g => g.id === goalId);
        set(state => ({
          savingsContributions: state.savingsContributions.filter(c => c.id !== id),
          savingsGoals: goal
            ? state.savingsGoals.map(g => g.id === goalId ? { ...g, currentAmount: Math.max(0, g.currentAmount - amount) } : g)
            : state.savingsGoals,
        }));
        if (goal) {
          const updated = { ...goal, currentAmount: Math.max(0, goal.currentAmount - amount) };
          try { await dbSync.syncSavingsGoal(updated, 'update'); } catch (e) { console.error(e); }
        }
      },

      // --------------------------------------------
      // Categories - Supabase First
      // --------------------------------------------
      addCategory: async (newCategory) => {
        const category: Category = { id: generateId(), ...newCategory, icon: 'Folder' as unknown as LucideIcon };
        set(state => ({ categories: [category, ...state.categories] }));
        try {
          await dbSync.syncCategory(category, 'create');
        } catch (error) {
          console.error('Failed to sync category to Supabase:', error);
          set(state => ({ categories: state.categories.filter(c => c.id !== category.id) }));
          throw error;
        }
      },

      updateCategory: async (updatedCategory) => {
        set(state => ({ 
          categories: state.categories.map(c => c.id === updatedCategory.id ? updatedCategory : c)
        }));
        try {
          await dbSync.syncCategory(updatedCategory, 'update');
        } catch (error) {
          console.error('Failed to update category in Supabase:', error);
          throw error;
        }
      },

      deleteCategory: async (id: string) => {
        const categoryToDelete = get().categories.find(c => c.id === id);
        if (!categoryToDelete) return;
        set(state => ({ categories: state.categories.filter(c => c.id !== id) }));
        try {
          await dbSync.syncCategory(categoryToDelete, 'delete');
        } catch (error) {
          console.error('Failed to delete category from Supabase:', error);
          set(state => ({ categories: [categoryToDelete, ...state.categories] }));
          throw error;
        }
      },

      // --------------------------------------------
      // Clients - Supabase First
      // --------------------------------------------
      addClient: async (newClient) => {
        const client: Client = { id: generateId(), ...newClient };
        set(state => ({ clients: [client, ...state.clients] }));
        try {
          await dbSync.syncClient(client, 'create');
        } catch (error) {
          console.error('Failed to sync client to Supabase:', error);
          set(state => ({ clients: state.clients.filter(c => c.id !== client.id) }));
          throw error;
        }
      },

      updateClient: async (updatedClient) => {
        set(state => ({ 
          clients: state.clients.map(c => c.id === updatedClient.id ? updatedClient : c)
        }));
        try {
          await dbSync.syncClient(updatedClient, 'update');
        } catch (error) {
          console.error('Failed to update client in Supabase:', error);
          throw error;
        }
      },

      deleteClient: async (id: string) => {
        const clientToDelete = get().clients.find(c => c.id === id);
        if (!clientToDelete) return;
        set(state => ({ clients: state.clients.filter(c => c.id !== id) }));
        try {
          await dbSync.syncClient(clientToDelete, 'delete');
        } catch (error) {
          console.error('Failed to delete client from Supabase:', error);
          set(state => ({ clients: [clientToDelete, ...state.clients] }));
          throw error;
        }
      },

      // --------------------------------------------
      // Products - Supabase First
      // --------------------------------------------
      addProduct: async (newProduct) => {
        const product: Product = { id: generateId(), ...newProduct };
        set(state => ({ products: [product, ...state.products] }));
        try {
          await dbSync.syncProduct(product, 'create');
        } catch (error) {
          console.error('Failed to sync product to Supabase:', error);
          set(state => ({ products: state.products.filter(p => p.id !== product.id) }));
          throw error;
        }
      },

      updateProduct: async (updatedProduct) => {
        set(state => ({ 
          products: state.products.map(p => p.id === updatedProduct.id ? updatedProduct : p)
        }));
        try {
          await dbSync.syncProduct(updatedProduct, 'update');
        } catch (error) {
          console.error('Failed to update product in Supabase:', error);
          throw error;
        }
      },

      deleteProduct: async (id: string) => {
        const productToDelete = get().products.find(p => p.id === id);
        if (!productToDelete) return;
        set(state => ({ products: state.products.filter(p => p.id !== id) }));
        try {
          await dbSync.syncProduct(productToDelete, 'delete');
        } catch (error) {
          console.error('Failed to delete product from Supabase:', error);
          set(state => ({ products: [productToDelete, ...state.products] }));
          throw error;
        }
      },

      // --------------------------------------------
      // Quotes - Supabase First
      // --------------------------------------------
      addQuote: async (newQuote) => {
        const quote: Quote = { id: generateId(), ...newQuote };
        set(state => ({ quotes: [quote, ...state.quotes] }));
        try {
          await dbSync.syncQuote(quote, 'create');
        } catch (error) {
          console.error('Failed to sync quote to Supabase:', error);
          set(state => ({ quotes: state.quotes.filter(q => q.id !== quote.id) }));
          throw error;
        }
      },

      updateQuote: async (updatedQuote) => {
        set(state => ({ 
          quotes: state.quotes.map(q => q.id === updatedQuote.id ? updatedQuote : q)
        }));
        try {
          await dbSync.syncQuote(updatedQuote, 'update');
        } catch (error) {
          console.error('Failed to update quote in Supabase:', error);
          throw error;
        }
      },

      deleteQuote: async (id: string) => {
        const quoteToDelete = get().quotes.find(q => q.id === id);
        if (!quoteToDelete) return;
        set(state => ({ quotes: state.quotes.filter(q => q.id !== id) }));
        try {
          await dbSync.syncQuote(quoteToDelete, 'delete');
        } catch (error) {
          console.error('Failed to delete quote from Supabase:', error);
          set(state => ({ quotes: [quoteToDelete, ...state.quotes] }));
          throw error;
        }
      },

      // --------------------------------------------
      // Loans - Supabase First
      // --------------------------------------------
      addLoan: async (newLoan) => {
        const loan: Loan = { id: generateId(), ...newLoan };
        set(state => ({ loans: [loan, ...state.loans] }));
        try {
          await dbSync.syncLoan(loan, 'create');
        } catch (error) {
          console.error('Failed to sync loan to Supabase:', error);
          set(state => ({ loans: state.loans.filter(l => l.id !== loan.id) }));
          throw error;
        }
      },

      updateLoan: async (updatedLoan) => {
        set(state => ({ 
          loans: state.loans.map(l => l.id === updatedLoan.id ? updatedLoan : l)
        }));
        try {
          await dbSync.syncLoan(updatedLoan, 'update');
        } catch (error) {
          console.error('Failed to update loan in Supabase:', error);
          throw error;
        }
      },

      deleteLoan: async (id: string) => {
        const loanToDelete = get().loans.find(l => l.id === id);
        if (!loanToDelete) return;
        set(state => ({ loans: state.loans.filter(l => l.id !== id) }));
        try {
          await dbSync.syncLoan(loanToDelete, 'delete');
        } catch (error) {
          console.error('Failed to delete loan from Supabase:', error);
          set(state => ({ loans: [loanToDelete, ...state.loans] }));
          throw error;
        }
      },
      
      // --------------------------------------------
      // Business Budgets - Supabase First
      // --------------------------------------------
      addBusinessBudget: async (newBudget) => {
        const budget: BusinessBudget = { id: generateId(), ...newBudget };
        set(state => ({ businessBudgets: [budget, ...state.businessBudgets] }));
        try {
          await dbSync.syncBusinessBudget(budget, 'create');
        } catch (error) {
          console.error('Failed to sync business budget to Supabase:', error);
          set(state => ({ businessBudgets: state.businessBudgets.filter(b => b.id !== budget.id) }));
          throw error;
        }
      },

      updateBusinessBudget: async (updatedBudget) => {
        set(state => ({ 
          businessBudgets: state.businessBudgets.map(b => b.id === updatedBudget.id ? updatedBudget : b)
        }));
        try {
          await dbSync.syncBusinessBudget(updatedBudget, 'update');
        } catch (error) {
          console.error('Failed to update business budget in Supabase:', error);
          throw error;
        }
      },

      deleteBusinessBudget: async (id: string) => {
        const budgetToDelete = get().businessBudgets.find(b => b.id === id);
        if (!budgetToDelete) return;
        set(state => ({ businessBudgets: state.businessBudgets.filter(b => b.id !== id) }));
        try {
          await dbSync.syncBusinessBudget(budgetToDelete, 'delete');
        } catch (error) {
          console.error('Failed to delete business budget from Supabase:', error);
          set(state => ({ businessBudgets: [budgetToDelete, ...state.businessBudgets] }));
          throw error;
        }
      },

      addBusinessRevenue: async (newRevenue) => {
        const revenue: BusinessRevenue = { id: generateId(), ...newRevenue };
        set(state => ({ businessRevenues: [revenue, ...state.businessRevenues] }));
        try { await dbSync.syncBusinessRevenue(revenue, 'create'); } catch (error) {
          set(state => ({ businessRevenues: state.businessRevenues.filter(r => r.id !== revenue.id) })); throw error;
        }
      },
      updateBusinessRevenue: async (updatedRevenue) => {
        set(state => ({ businessRevenues: state.businessRevenues.map(r => r.id === updatedRevenue.id ? updatedRevenue : r) }));
        try { await dbSync.syncBusinessRevenue(updatedRevenue, 'update'); } catch (error) { throw error; }
      },
      deleteBusinessRevenue: async (id: string) => {
        const item = get().businessRevenues.find(r => r.id === id);
        if (!item) return;
        set(state => ({ businessRevenues: state.businessRevenues.filter(r => r.id !== id) }));
        try { await dbSync.syncBusinessRevenue(item, 'delete'); } catch (error) {
          set(state => ({ businessRevenues: [item, ...state.businessRevenues] })); throw error;
        }
      },
      addBusinessExpense: async (newExpense) => {
        const expense: BusinessExpense = { id: generateId(), ...newExpense };
        set(state => ({ businessExpenses: [expense, ...state.businessExpenses] }));
        try { await dbSync.syncBusinessExpense(expense, 'create'); } catch (error) {
          set(state => ({ businessExpenses: state.businessExpenses.filter(e => e.id !== expense.id) })); throw error;
        }
      },
      updateBusinessExpense: async (updatedExpense) => {
        set(state => ({ businessExpenses: state.businessExpenses.map(e => e.id === updatedExpense.id ? updatedExpense : e) }));
        try { await dbSync.syncBusinessExpense(updatedExpense, 'update'); } catch (error) { throw error; }
      },
      deleteBusinessExpense: async (id: string) => {
        const item = get().businessExpenses.find(e => e.id === id);
        if (!item) return;
        set(state => ({ businessExpenses: state.businessExpenses.filter(e => e.id !== id) }));
        try { await dbSync.syncBusinessExpense(item, 'delete'); } catch (error) {
          set(state => ({ businessExpenses: [item, ...state.businessExpenses] })); throw error;
        }
      },

      // --------------------------------------------
      // Group Savings - Supabase First
      // --------------------------------------------
      addSavingsGroup: async (newGroup) => {
        const group: SavingsGroup = { id: generateId(), ...newGroup };
        set(state => ({ savingsGroups: [group, ...state.savingsGroups] }));
        try {
          await dbSync.syncSavingsGroup(group, 'create');
        } catch (error) {
          console.error('Failed to sync savings group to Supabase:', error);
          set(state => ({ savingsGroups: state.savingsGroups.filter(g => g.id !== group.id) }));
          throw error;
        }
      },

      updateSavingsGroup: async (updatedGroup) => {
        set(state => ({ 
          savingsGroups: state.savingsGroups.map(g => g.id === updatedGroup.id ? updatedGroup : g)
        }));
        try {
          await dbSync.syncSavingsGroup(updatedGroup, 'update');
        } catch (error) {
          console.error('Failed to update savings group in Supabase:', error);
          throw error;
        }
      },

      deleteSavingsGroup: async (id: string) => {
        const groupToDelete = get().savingsGroups.find(g => g.id === id);
        if (!groupToDelete) return;
        set(state => ({ savingsGroups: state.savingsGroups.filter(g => g.id !== id) }));
        try {
          await dbSync.syncSavingsGroup(groupToDelete, 'delete');
        } catch (error) {
          console.error('Failed to delete savings group from Supabase:', error);
          set(state => ({ savingsGroups: [groupToDelete, ...state.savingsGroups] }));
          throw error;
        }
      },

      addGroupMember: async (newMember) => {
        const member: GroupMember = { id: generateId(), ...newMember };
        set(state => ({ groupMembers: [member, ...state.groupMembers] }));
        try { await dbSync.syncGroupMember(member, 'create'); } catch (error) {
          console.error('Failed to sync group member to Supabase:', error);
          set(state => ({ groupMembers: state.groupMembers.filter(m => m.id !== member.id) }));
          throw error;
        }
      },

      updateGroupMember: async (updatedMember) => {
        set(state => ({ groupMembers: state.groupMembers.map(m => m.id === updatedMember.id ? updatedMember : m) }));
        try { await dbSync.syncGroupMember(updatedMember, 'update'); } catch (error) {
          console.error('Failed to update group member in Supabase:', error);
          throw error;
        }
      },

      deleteGroupMember: async (id: string) => {
        const memberToDelete = get().groupMembers.find(m => m.id === id);
        if (!memberToDelete) return;
        set(state => ({ groupMembers: state.groupMembers.filter(m => m.id !== id) }));
        try { await dbSync.syncGroupMember(memberToDelete, 'delete'); } catch (error) {
          console.error('Failed to delete group member from Supabase:', error);
          set(state => ({ groupMembers: [memberToDelete, ...state.groupMembers] }));
          throw error;
        }
      },
      
      addGroupInvitation: async (newInvitation) => {
        const invitation: GroupInvitation = { id: generateId(), ...newInvitation };
        set(state => ({ groupInvitations: [invitation, ...state.groupInvitations] }));
        try { await dbSync.syncGroupInvitation(invitation, 'create'); } catch (error) {
          console.error('Failed to sync group invitation to Supabase:', error);
          set(state => ({ groupInvitations: state.groupInvitations.filter(i => i.id !== invitation.id) }));
          throw error;
        }
      },

      updateGroupInvitation: async (updatedInvitation) => {
        set(state => ({ groupInvitations: state.groupInvitations.map(i => i.id === updatedInvitation.id ? updatedInvitation : i) }));
        try { await dbSync.syncGroupInvitation(updatedInvitation, 'update'); } catch (error) {
          console.error('Failed to update group invitation in Supabase:', error);
          throw error;
        }
      },

      deleteGroupInvitation: async (id: string) => {
        const invitationToDelete = get().groupInvitations.find(i => i.id === id);
        if (!invitationToDelete) return;
        set(state => ({ groupInvitations: state.groupInvitations.filter(i => i.id !== id) }));
        try { await dbSync.syncGroupInvitation(invitationToDelete, 'delete'); } catch (error) {
          console.error('Failed to delete group invitation from Supabase:', error);
          set(state => ({ groupInvitations: [invitationToDelete, ...state.groupInvitations] }));
          throw error;
        }
      },
      
      addGroupContribution: async (newContribution) => {
        const contribution: GroupContribution = { id: generateId(), ...newContribution };
        set(state => ({ groupContributions: [contribution, ...state.groupContributions] }));
        try { await dbSync.syncGroupContribution(contribution, 'create'); } catch (error) {
          console.error('Failed to sync group contribution to Supabase:', error);
          set(state => ({ groupContributions: state.groupContributions.filter(c => c.id !== contribution.id) }));
          throw error;
        }
      },

      updateGroupContribution: async (updatedContribution) => {
        set(state => ({ groupContributions: state.groupContributions.map(c => c.id === updatedContribution.id ? updatedContribution : c) }));
        try { await dbSync.syncGroupContribution(updatedContribution, 'update'); } catch (error) {
          console.error('Failed to update group contribution in Supabase:', error);
          throw error;
        }
      },

      deleteGroupContribution: async (id: string) => {
        const contributionToDelete = get().groupContributions.find(c => c.id === id);
        if (!contributionToDelete) return;
        set(state => ({ groupContributions: state.groupContributions.filter(c => c.id !== id) }));
        try { await dbSync.syncGroupContribution(contributionToDelete, 'delete'); } catch (error) {
          console.error('Failed to delete group contribution from Supabase:', error);
          set(state => ({ groupContributions: [contributionToDelete, ...state.groupContributions] }));
          throw error;
        }
      },
      
      addGroupActivity: async (newActivity) => {
        const activity: GroupActivity = { id: generateId(), ...newActivity };
        set(state => ({ groupActivities: [activity, ...state.groupActivities] }));
        try { await dbSync.syncGroupActivity(activity, 'create'); } catch (error) {
          console.error('Failed to sync group activity to Supabase:', error);
          set(state => ({ groupActivities: state.groupActivities.filter(a => a.id !== activity.id) }));
          throw error;
        }
      },

      updateGroupActivity: async (updatedActivity: GroupActivity) => {
        set(state => ({ groupActivities: state.groupActivities.map(a => a.id === updatedActivity.id ? updatedActivity : a) }));
        try { await dbSync.syncGroupActivity(updatedActivity, 'update'); } catch (error) {
          console.error('Failed to update group activity in Supabase:', error);
          throw error;
        }
      },

      deleteGroupActivity: async (id: string) => {
        const activityToDelete = get().groupActivities.find(a => a.id === id);
        if (!activityToDelete) return;
        set(state => ({ groupActivities: state.groupActivities.filter(a => a.id !== id) }));
        try { await dbSync.syncGroupActivity(activityToDelete, 'delete'); } catch (error) {
          console.error('Failed to delete group activity from Supabase:', error);
          set(state => ({ groupActivities: [activityToDelete, ...state.groupActivities] }));
          throw error;
        }
      },

      addProject: async (newProject: Omit<Project, 'id'>) => {
        const project: Project = { id: generateId(), ...newProject };
        set(state => ({ projects: [project, ...state.projects] }));
        try { await dbSync.syncProject(project, 'create'); } catch (error) {
          console.error('Failed to sync project to Supabase:', error);
          set(state => ({ projects: state.projects.filter(p => p.id !== project.id) }));
          throw error;
        }
      },

      updateProject: async (updatedProject: Project) => {
        set(state => ({ projects: state.projects.map(p => p.id === updatedProject.id ? updatedProject : p) }));
        try { await dbSync.syncProject(updatedProject, 'update'); } catch (error) {
          console.error('Failed to update project in Supabase:', error);
          throw error;
        }
      },

      deleteProject: async (id: string) => {
        const projectToDelete = get().projects.find(p => p.id === id);
        if (!projectToDelete) return;
        set(state => ({ projects: state.projects.filter(p => p.id !== id) }));
        try { await dbSync.syncProject(projectToDelete, 'delete'); } catch (error) {
          console.error('Failed to delete project from Supabase:', error);
          set(state => ({ projects: [projectToDelete, ...state.projects] }));
          throw error;
        }
      },

      addInvoice: async (newInvoice: Omit<Invoice, 'id'>) => {
        const invoice: Invoice = { id: generateId(), ...newInvoice };
        set(state => ({ invoices: [invoice, ...state.invoices] }));
        try { await dbSync.syncInvoice(invoice, 'create'); } catch (error) {
          console.error('Failed to sync invoice to Supabase:', error);
          set(state => ({ invoices: state.invoices.filter(i => i.id !== invoice.id) }));
          throw error;
        }
      },

      updateInvoice: async (updatedInvoice: Invoice) => {
        set(state => ({ invoices: state.invoices.map(i => i.id === updatedInvoice.id ? updatedInvoice : i) }));
        try { await dbSync.syncInvoice(updatedInvoice, 'update'); } catch (error) {
          console.error('Failed to update invoice in Supabase:', error);
          throw error;
        }
      },

      deleteInvoice: async (id: string) => {
        const invoiceToDelete = get().invoices.find(i => i.id === id);
        if (!invoiceToDelete) return;
        set(state => ({ invoices: state.invoices.filter(i => i.id !== id) }));
        try { await dbSync.syncInvoice(invoiceToDelete, 'delete'); } catch (error) {
          console.error('Failed to delete invoice from Supabase:', error);
          set(state => ({ invoices: [invoiceToDelete, ...state.invoices] }));
          throw error;
        }
      },

      addClientPayment: async (newPayment: Omit<ClientPayment, 'id'>) => {
        const payment: ClientPayment = { id: generateId(), ...newPayment };
        set(state => ({ payments: [payment, ...state.payments] }));
        try { await dbSync.syncClientPayment(payment, 'create'); } catch (error) {
          console.error('Failed to sync client payment to Supabase:', error);
          set(state => ({ payments: state.payments.filter(p => p.id !== payment.id) }));
          throw error;
        }
      },

      updateClientPayment: async (updatedPayment: ClientPayment) => {
        set(state => ({ payments: state.payments.map(p => p.id === updatedPayment.id ? updatedPayment : p) }));
        try { await dbSync.syncClientPayment(updatedPayment, 'update'); } catch (error) {
          console.error('Failed to update client payment in Supabase:', error);
          throw error;
        }
      },

      deleteClientPayment: async (id: string) => {
        const paymentToDelete = get().payments.find(p => p.id === id);
        if (!paymentToDelete) return;
        set(state => ({ payments: state.payments.filter(p => p.id !== id) }));
        try { await dbSync.syncClientPayment(paymentToDelete, 'delete'); } catch (error) {
          console.error('Failed to delete client payment from Supabase:', error);
          set(state => ({ payments: [paymentToDelete, ...state.payments] }));
          throw error;
        }
      },

      addClientExpense: async (newExpense: Omit<ClientExpense, 'id'>) => {
        const expense: ClientExpense = { id: generateId(), ...newExpense };
        set(state => ({ expenses: [expense, ...state.expenses] }));
        try { await dbSync.syncClientExpense(expense, 'create'); } catch (error) {
          console.error('Failed to sync client expense to Supabase:', error);
          set(state => ({ expenses: state.expenses.filter(e => e.id !== expense.id) }));
          throw error;
        }
      },

      updateClientExpense: async (updatedExpense: ClientExpense) => {
        set(state => ({ expenses: state.expenses.map(e => e.id === updatedExpense.id ? updatedExpense : e) }));
        try { await dbSync.syncClientExpense(updatedExpense, 'update'); } catch (error) {
          console.error('Failed to update client expense in Supabase:', error);
          throw error;
        }
      },

      deleteClientExpense: async (id: string) => {
        const expenseToDelete = get().expenses.find(e => e.id === id);
        if (!expenseToDelete) return;
        set(state => ({ expenses: state.expenses.filter(e => e.id !== id) }));
        try { await dbSync.syncClientExpense(expenseToDelete, 'delete'); } catch (error) {
          console.error('Failed to delete client expense from Supabase:', error);
          set(state => ({ expenses: [expenseToDelete, ...state.expenses] }));
          throw error;
        }
      },

      addCommunicationLog: async (newLog: Omit<CommunicationLog, 'id'>) => {
        const log: CommunicationLog = { id: generateId(), ...newLog };
        set(state => ({ communications: [log, ...state.communications] }));
        try { await dbSync.syncCommunicationLog(log, 'create'); } catch (error) {
          console.error('Failed to sync communication log to Supabase:', error);
          set(state => ({ communications: state.communications.filter(c => c.id !== log.id) }));
          throw error;
        }
      },

      updateCommunicationLog: async (updatedLog: CommunicationLog) => {
        set(state => ({ communications: state.communications.map(c => c.id === updatedLog.id ? updatedLog : c) }));
        try { await dbSync.syncCommunicationLog(updatedLog, 'update'); } catch (error) {
          console.error('Failed to update communication log in Supabase:', error);
          throw error;
        }
      },

      deleteCommunicationLog: async (id: string) => {
        const logToDelete = get().communications.find(c => c.id === id);
        if (!logToDelete) return;
        set(state => ({ communications: state.communications.filter(c => c.id !== id) }));
        try { await dbSync.syncCommunicationLog(logToDelete, 'delete'); } catch (error) {
          console.error('Failed to delete communication log from Supabase:', error);
          set(state => ({ communications: [logToDelete, ...state.communications] }));
          throw error;
        }
      },

      addTaskNote: async (newTask: Omit<TaskNote, 'id'>) => {
        const task: TaskNote = { id: generateId(), ...newTask };
        set(state => ({ tasks: [task, ...state.tasks] }));
        try { await dbSync.syncTaskNote(task, 'create'); } catch (error) {
          console.error('Failed to sync task note to Supabase:', error);
          set(state => ({ tasks: state.tasks.filter(t => t.id !== task.id) }));
          throw error;
        }
      },

      updateTaskNote: async (updatedTask: TaskNote) => {
        set(state => ({ tasks: state.tasks.map(t => t.id === updatedTask.id ? updatedTask : t) }));
        try { await dbSync.syncTaskNote(updatedTask, 'update'); } catch (error) {
          console.error('Failed to update task note in Supabase:', error);
          throw error;
        }
      },

      deleteTaskNote: async (id: string) => {
        const taskToDelete = get().tasks.find(t => t.id === id);
        if (!taskToDelete) return;
        set(state => ({ tasks: state.tasks.filter(t => t.id !== id) }));
        try { await dbSync.syncTaskNote(taskToDelete, 'delete'); } catch (error) {
          console.error('Failed to delete task note from Supabase:', error);
          set(state => ({ tasks: [taskToDelete, ...state.tasks] }));
          throw error;
        }
      },
    }),
    {
      name: 'kwachalite-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        transactions: state.transactions,
        bills: state.bills,
        savingsGoals: state.savingsGoals,
        clients: state.clients,
        products: state.products,
        quotes: state.quotes,
        loans: state.loans,
        categories: state.categories,
        salesReceipts: state.salesReceipts,
        deliveryNotes: state.deliveryNotes,
        businessBudgets: state.businessBudgets,
        businessRevenues: state.businessRevenues,
        businessExpenses: state.businessExpenses,
        savingsGroups: state.savingsGroups,
        groupMembers: state.groupMembers,
        groupInvitations: state.groupInvitations,
        groupContributions: state.groupContributions,
        groupActivities: state.groupActivities,
        projects: state.projects,
        invoices: state.invoices,
        payments: state.payments,
        expenses: state.expenses,
        communications: state.communications,
        tasks: state.tasks,
      }) as Partial<AppState>,
    }
  )
);

// Expose store to window for Supabase sync
if (typeof window !== 'undefined') {
  (window as any).__KWACHALITE_STORE__ = {
    setSyncData: (key: string, data: any[]) => {
      useAppStore.getState().setSyncData(key, data);
    },
  };
}
